'use client'

import {logger} from 'common'
import {NextIntlClientProvider, useMessages} from 'next-intl'
import {FC, useEffect, useMemo, useState} from 'react'

import {SnackbarType} from '@/components/common/snackbar/constants'
import {useSnackbarContext} from '@/components/common/snackbar/context/SnackbarContext'
import {getCommonContent} from '@/content/commonContent'
import {FaqCategories} from '@/content/FAQs'
import {useTranslation} from '@/i18n'
import {Locale} from '@/utils/constants'
import {useLanguage} from '@/utils/i18n/language'
import {loadNamespace} from '@/utils/i18n/loadNamespace'

import {useHelpCenterContext} from './context/HelpCenterContext'

const NAMESPACE = 'help-center'

type HelpCenterLazyProps = {sortedCategories?: FaqCategories}

type Loaded = {
  HelpCenter: FC<HelpCenterLazyProps>
  messages: Record<string, any>
}

// Module-level, so a client-side navigation reuses what the previous page loaded
let helpCenterImport: Promise<FC<HelpCenterLazyProps>> | null = null
const namespaceLoads: Partial<Record<Locale, Promise<Record<string, any>>>> = {}

const importHelpCenter = () => {
  helpCenterImport ??= import('./HelpCenter').then(({HelpCenter}) => HelpCenter)
  return helpCenterImport
}

const loadMessages = (locale: Locale) => {
  namespaceLoads[locale] ??= loadNamespace(locale, NAMESPACE)
  return namespaceLoads[locale]
}

// Neither the drawer's code nor its translations load until it is about to open (help button
// hovered or focused, or anything opening it). /faq passes the namespace down already.
export const HelpCenterLazy: FC<HelpCenterLazyProps> = ({sortedCategories}) => {
  const language = useLanguage()
  const messages = useMessages()
  const {t} = useTranslation()
  const {isActive, setIsActive, setActiveFaq, isLoadRequested} = useHelpCenterContext()
  const {showSnackbar} = useSnackbarContext()
  const [loaded, setLoaded] = useState<Loaded | null>(null)

  const hasNamespace = NAMESPACE in messages
  const errorText = t(getCommonContent().apiErrorMessage)

  useEffect(() => {
    if (!(isLoadRequested || isActive) || loaded) {
      return
    }

    const load = async () => {
      try {
        const [HelpCenter, namespaceMessages] = await Promise.all([
          importHelpCenter(),
          hasNamespace ? {} : loadMessages(language),
        ])
        setLoaded({HelpCenter, messages: namespaceMessages})
      } catch (error) {
        // Forget the failed load, so the next attempt starts over
        helpCenterImport = null
        delete namespaceLoads[language]
        logger.error(error, '[HelpCenterLazy] Failed to load the help center')

        // Only when the user tried to open it, not on a hover prefetch. Closing the drawer that
        // never rendered, and dropping the FAQ it was opened on, lets the next click retry.
        if (isActive) {
          setIsActive(false)
          setActiveFaq(null)
          showSnackbar(errorText, true, SnackbarType.error)
        }
      }
    }

    void load()
  }, [
    isLoadRequested,
    isActive,
    loaded,
    hasNamespace,
    language,
    setIsActive,
    setActiveFaq,
    showSnackbar,
    errorText,
  ])

  // A nested provider replaces the messages it inherits, so the page's own are passed on too
  const mergedMessages = useMemo(
    () => (loaded ? {...messages, ...loaded.messages} : messages),
    [loaded, messages],
  )

  if (!loaded) {
    return null
  }

  const {HelpCenter} = loaded

  return (
    <NextIntlClientProvider locale={language} messages={mergedMessages}>
      <HelpCenter sortedCategories={sortedCategories} />
    </NextIntlClientProvider>
  )
}
